import {observer} from 'mobx-react';
import {ReactNode, useContext, useEffect, useMemo} from 'react';
import {NetworkContext} from '../../../lib/context';
import NOFilter from '../no-filter';
import {useParams} from 'react-router-dom';

type Props = {
    addCurrentRosterToSelection?: boolean;
    maxSelectableRosters?: number;
};

function groupAndSortByRosterId(data: any[], rosterId?: string) {
    let filteredData = [...data];
    if (rosterId) {
        filteredData = filteredData.filter((item) => item.rosterId === rosterId);
    }
    // Group by rosterId
    const grouped = filteredData.reduce((acc, obj) => {
        if (!acc[obj.rosterId]) acc[obj.rosterId] = [];
        acc[obj.rosterId].push(obj);
        return acc;
    }, {});

    // Flatten and sort by revisionId within each group
    return Object.values(grouped)
        .map((group: any) =>
            group.sort((a: any, b: any) => Number(a.revisionId) - Number(b.revisionId)),
        )
        .flat()
        .map((item: any) => ({
            rosterId: item.rosterId,
            revisionId: item.revisionId,
            revisionName: item.revisionName,
            rosterName: item.rosterName,
            originalVsAdded: item.revisionId === '0' ? 'Original' : 'Revised',
        }));
}

const RosterSelection = function (props: Props) {
    const {addCurrentRosterToSelection} = props;
    const {rosterId, revisionId} = useParams();
    const networkOptimizationStore = useContext(NetworkContext);

    useEffect(() => {
        const controller = new AbortController();

        // Only load rosters if they haven't been loaded yet
        if (
            networkOptimizationStore.providerRosters.length === 0 &&
            !networkOptimizationStore.loadingProviderRosters
        ) {
            networkOptimizationStore.loadProviderRosters(controller);
        }

        return () => controller.abort();
    }, []);

    useEffect(() => {
        // Add current roster to selection if needed
        if (
            addCurrentRosterToSelection &&
            !networkOptimizationStore.selectedRosters.some(
                (r: any) => r.rosterId === rosterId && r.revisionId === revisionId,
            )
        ) {
            networkOptimizationStore.selectedRosters = [
                ...networkOptimizationStore.selectedRosters,
                {
                    rosterId,
                    revisionId,
                },
            ];
        }
    }, [addCurrentRosterToSelection]);

    const revisionIdSelectorData = useMemo(() => {
        return groupAndSortByRosterId(networkOptimizationStore.providerRosters || []).filter(
            (item) => item.rosterId !== rosterId || item.revisionId !== revisionId,
        );
    }, [networkOptimizationStore.providerRosters, revisionId, rosterId]);

    const readyToLoadData = useMemo(() => {
        return (
            ((networkOptimizationStore.appliedGeographyFilter.standard_type &&
                networkOptimizationStore.appliedGeographyFilter.standard_version) ||
                networkOptimizationStore.appliedDisruptionAnalysisFilter.standard_type) &&
            networkOptimizationStore.runId
        );
    }, [
        networkOptimizationStore.appliedGeographyFilter.standard_type,
        networkOptimizationStore.appliedGeographyFilter.standard_version,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.standard_type,
        networkOptimizationStore.runId,
    ]);

    const revisionIdSelector = useMemo(() => {
        const onChange = (value: string) => {
            if (!value) {
                if (rosterId)
                    networkOptimizationStore.selectedRosters = [
                        {
                            rosterId,
                            revisionId,
                        },
                    ];
            } else {
                const currSelectedRosterId = value.split('-')[0];
                const currSelectedRevisionId = value.split('-')[1];
                if (currSelectedRosterId !== rosterId || currSelectedRevisionId !== revisionId) {
                    networkOptimizationStore.selectedRosters = [
                        {
                            rosterId,
                            revisionId,
                        },
                        {
                            rosterId: currSelectedRosterId,
                            revisionId: currSelectedRevisionId,
                        },
                    ];
                }
            }
            networkOptimizationStore.selectedRosterForComparison = value;
        };

        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            // Return the value as string for identification
            (item: any): string => {
                return `${item.rosterId.toString()}-${item.revisionId.toString()}`;
            },
            // Render the display content
            (item: any): ReactNode => {
                return `${item.originalVsAdded} | ${item.rosterName}${
                    item.revisionId !== '0' ? ` - ${item.revisionName}` : ''
                }`;
            },
        ];

        return {
            label: 'Comparable Rosters',
            data: revisionIdSelectorData,
            value: networkOptimizationStore.selectedRosterForComparison,
            onChange,
            children,
            spinner: networkOptimizationStore.providerRosters.length === 0 || !readyToLoadData,
            disabled: false,
        };
    }, [
        networkOptimizationStore.revisionId,
        networkOptimizationStore.providerRosters,
        revisionIdSelectorData,
        networkOptimizationStore.selectedRosterForComparison,
        readyToLoadData,
    ]);

    return (
        <div className='mb-4'>
            <NOFilter
                label={revisionIdSelector.label}
                searchable
                spinner={revisionIdSelector.spinner}
                data-testid='comparable-rosters-dropdown'
                data={revisionIdSelector.data}
                value={revisionIdSelector.value}
                onChange={revisionIdSelector.onChange}
                disabled={revisionIdSelector.disabled}
                itemToString={(item) =>
                    `${item.originalVsAdded} | ${item.rosterName}${
                        item.revisionId !== '0' ? ` - ${item.revisionName}` : ''
                    }`
                }
            >
                {revisionIdSelector.children}
            </NOFilter>
        </div>
    );
};

export default observer(RosterSelection);
